/* eslint-disable @typescript-eslint/no-unused-vars */
import * as Yup from "yup";
import { Exercise } from "../services/exerciseService";

export const exerciseValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Exercise name is required")
    .matches(/^(?!\s*$).+/, "Exercise name cannot be empty or just spaces"),
  workoutCategory: Yup.array().of(Yup.string()),
  workoutProgram: Yup.array().of(Yup.string()),
  images: Yup.array()
    .of(Yup.string().required("Image is required"))
    .required("Images are required"),
  videos: Yup.array()
    .of(
      Yup.object().shape({
        url: Yup.string().required("Video URL is required"),
      })
    )
    .min(1, "At least one video is required")
    .required("Videos are required"),
  targetMuscles: Yup.string().required("Target muscles are required"),
  gender: Yup.array().of(Yup.string()).min(1, "Gender is required"),
  fitnessLevel: Yup.array()
    .of(Yup.string())
    .min(1, "Fitness Level is required")
    .required(),
  priority: Yup.string().required("Exercise priority is required"),
  mood: Yup.array()
    .of(Yup.string())
    .min(1, "Fitness mood is required")
    .required(),
  fitnessGoal: Yup.array()
    .of(Yup.string())
    .min(1, "Fitness goal is required")
    .required(),
  description: Yup.string()
    .required("Instructions are required")
    .test("html-content-check", "Instructions cannot be empty", (value) => {
      const plainText = value?.replace(/<[^>]*>?/gm, "").trim();
      return !!plainText && plainText.length > 0;
    })
    .test(
      "max-length-check",
      "Instructions cannot exceed 1000 characters",
      (value) => {
        if (!value) return true;
        const plainText = value.replace(/<[^>]*>?/gm, "").trim();
        return plainText.length <= 1000;
      }
    ),
  exerciseType: Yup.string()
    .required("Exercise type is required")
    .notOneOf(["Clear"], "Please select an exercise type"),
  equipmentIds: Yup.array().of(Yup.string()),
  workingSets: Yup.array()
    .of(
      Yup.object().shape({
        set: Yup.number().required("Set number is required"),
        kg: Yup.string().when("exerciseType", {
          is: "weight_and_reps_based",
          then: () => Yup.string().required("Kg is required"),
        }),
        reps: Yup.string().when("exerciseType", {
          is: (val: string) => val === "weight_and_reps_based" || val === "bodyweight",
          then: () => Yup.string().required("Reps are required"),
        }),
        time: Yup.string().when("exerciseType", {
          is: "time_based",
          then: () => Yup.string().required("Time is required"),
        }),
        meters: Yup.string().when("exerciseType", {
          is: "distance_based",
          then: () => Yup.string().required("Meters are required"),
        }),
      })
    )
    .min(1, "At least one working set is required")
    .required("Working sets are required"),
});

export const editExerciseValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Exercise name is required")
    .matches(/^(?!\s*$).+/, "Exercise name cannot be empty or just spaces"),
  workoutCategory: Yup.array()
    .of(Yup.string())
    .min(1, "At least one workout category is required")
    .required("Workout category is required"),
  workoutProgram: Yup.array()
    .of(Yup.string())
    .min(1, "At least one workout program is required")
    .required("Workout program is required"),
  images: Yup.array().of(Yup.string()).required("Images are required"),
  videos: Yup.array()
    .of(
      Yup.object().shape({
        url: Yup.string(),
      })
    )
    .min(1, "At least one video is required")
    .required("Videos are required"),
  targetMuscles: Yup.string().required("Target muscles are required"),
  gender: Yup.array()
    .of(Yup.string())
    .min(1, "At least one gender is required")
    .required("Gender is required"),
  fitnessLevel: Yup.array()
    .of(Yup.string())
    .min(1, "At least one fitness level is required")
    .required("Fitness level is required"),
  priority: Yup.string().required("Exercise priority is required"),
  mood: Yup.array()
    .of(Yup.string())
    .min(1, "At least one fitness mood is required")
    .required("Fitness mood is required"),
  fitnessGoal: Yup.array()
    .of(Yup.string())
    .min(1, "At least one fitness goal is required")
    .required("Fitness goal is required"),
  description: Yup.string()
    .required("Instructions are required")
    .test("html-content-check", "Instructions cannot be empty", (value) => {
      const plainText = value?.replace(/<[^>]*>?/gm, "").trim();
      return !!plainText && plainText.length > 0;
    })
    .test(
      "max-length-check",
      "Instructions cannot exceed 1000 characters",
      (value) => {
        if (!value) return true;
        const plainText = value.replace(/<[^>]*>?/gm, "").trim();
        return plainText.length <= 1000;
      }
    ),
  exerciseType: Yup.string()
    .required("Exercise type is required")
    .notOneOf(["Clear"], "Please select an exercise type"),
  equipmentIds: Yup.array()
    .of(Yup.string())
    .min(1, "At least one equipment is required")
    .required("Equipment is required"),
  workingSets: Yup.array()
    .of(
      Yup.object().shape({
        set: Yup.number().required("Set number is required"),
        kg: Yup.string().when("exerciseType", {
          is: "weight_and_reps_based",
          then: () => Yup.string().required("Kg is required"),
        }),
        reps: Yup.string().when("exerciseType", {
          is: (val: string) => val === "weight_and_reps_based" || val === "bodyweight",
          then: () => Yup.string().required("Reps are required"),
        }),
        time: Yup.string().when("exerciseType", {
          is: "time_based",
          then: () => Yup.string().required("Time is required"),
        }),
        meters: Yup.string().when("exerciseType", {
          is: "distance_based",
          then: () => Yup.string().required("Meters are required"),
        }),
      })
    )
    .min(1, "At least one working set is required")
    .required("Working sets are required"),
});
